import * as React from 'react'
import {InventoryItem, InventoryPropertiesEnum, ItemQuantity} from "./Inventory";
import {useCallback, useEffect} from "react";
import {
    GameItems,
    ItemList,
    MINER_BEHAVIORS,
    POWER_CONSTANTS,
    SMELTER_RECIPES,
    TECHNOLOGIES,
    TIME_INCREMENT_MS,
    buildStartingInventory,
    CRAFTING_RECIPES,
    FUEL_ENERGY_VALUES,
    ENERGY_COSTS,
    FUEL_BUFFER_CAPACITY,
} from "./GameData";
import {accumulatePollution, POLLUTION_IMPACT} from "./Pollution";
import {AssemblySetup, AssemblyNode} from "../Assembly/AssemblyTypes";
import {ASSEMBLY_RECIPES} from "./AssemblyRecipes";
import {calculateSourceNodeOutputPerSecond} from "../utils/smelterCalculations";
import {
    normalizeActiveItem,
    ensureField,
    ensureInventoryItems,
    findInventoryQuantity,
} from "./inventoryHelpers";
import {
    calculateCompleteCycles,
    calculateMaxCyclesByFuel,
    calculateMaxCyclesByInput,
    consumeFuel,
    consumeInput,
    produceOutput,
    calculateRemainingProgress,
} from "./machineOperations";

export const COAL_MINER = "coal";
export const IRON_MINER = "iron";
export const COPPER_MINER = "copper";
export const STONE_MINER = "stone";

const ASSEMBLER_POWER_USE = 20;
const OVERCLOCK_SPEED = 2;
const OVERCLOCK_POWER = 4;

export type InventoryQuantity = {
    item: InventoryItem,
    quantity: number
}

export type ActiveItem = {
    itemId: string,
    running: boolean,
    progressMs: number,
    fuelStarved?: boolean,
    inputStarved?: boolean, // True when machine has no input resources (e.g., ore for smelters)
    fuelBuffer?: number, // Fuel stored in machine (in fuel units, up to 100)
}

export type MinerState = {
    resourceType: string,
    miners: ActiveItem[],
    clearedSpots: number,
    fuelType?: string,
}

export type SmelterState = {
    resourceType: string,
    smelters: ActiveItem[],
    fuelType: string | null,
    clearedSpots: number,
}

export type PowerSnapshot = {
    demand: number,
    supply: number,
    ratio: number,
    externalDemand: number,
    steamDemand: number,
    steamUsed: number,
    steamProduced: number,
    steamCapacity: number,
    waterProduced: number,
    waterUsed: number,
    fuelUsedPerSecond: number,
};

type PowerMachineState = {
    waterPumps: ActiveItem[],
    waterBoilers: ActiveItem[],
    steamGenerators: ActiveItem[],
};

type PowerMachineType = keyof PowerMachineState;

const POWER_LIMITS: Record<PowerMachineType, number> = {
    waterPumps: 1,
    waterBoilers: POWER_CONSTANTS.boilerStackSize * 3,
    steamGenerators: POWER_CONSTANTS.generatorStackSize * 3,
};

export type CraftingQueueItem = {
    id: string;              // Unique ID: `craft-${Date.now()}-${Math.random()}`
    recipeId: string;        // Recipe being crafted
    progressMs: number;      // Progress in milliseconds
    missingIngredients: boolean;  // True when top item lacks materials
}

export type GlobalInventory = {
    inventory: InventoryQuantity[],
    minerState: MinerState[],
    smelterState: SmelterState[],
    technologies: string[],
    powerMachines: PowerMachineState,
    powerStats: PowerSnapshot,
    pollution: number,
    assemblySetups: AssemblySetup[],
    fuelAmountPerClick?: number, // How much fuel to add per click (default 1)
    craftingQueue: CraftingQueueItem[],
}

const StartingInventory: InventoryQuantity[] = buildStartingInventory();

const initialMiners: MinerState[] = [
    {resourceType: COAL_MINER, miners: [], clearedSpots: 1, fuelType: undefined},
    {resourceType: IRON_MINER, miners: [], clearedSpots: 1, fuelType: undefined},
    {resourceType: COPPER_MINER, miners: [], clearedSpots: 1, fuelType: undefined},
    {resourceType: STONE_MINER, miners: [], clearedSpots: 1, fuelType: undefined},
];

const initialSmelters: SmelterState[] = [
    {resourceType: IRON_MINER, smelters: [], fuelType: null, clearedSpots: 1},
    {resourceType: "copper", smelters: [], fuelType: null, clearedSpots: 1},
    {resourceType: "stone", smelters: [], fuelType: null, clearedSpots: 1},
    {resourceType: "steel", smelters: [], fuelType: null, clearedSpots: 1},
];

const initialPowerMachines: PowerMachineState = {
    waterPumps: [{itemId: "waterPump", running: true, progressMs: 0}],
    waterBoilers: [],
    steamGenerators: [],
};

const initialPowerStats: PowerSnapshot = {
    demand: 0,
    supply: 0,
    ratio: 1,
    externalDemand: 0,
    steamDemand: 0,
    steamUsed: 0,
    steamProduced: 0,
    steamCapacity: 0,
    waterProduced: 0,
    waterUsed: 0,
    fuelUsedPerSecond: 0,
};

const initialGlobalInventory: GlobalInventory = {
    inventory: StartingInventory,
    minerState: initialMiners,
    smelterState: initialSmelters,
    technologies: [],
    powerMachines: initialPowerMachines,
    powerStats: initialPowerStats,
    pollution: 0,
    assemblySetups: [],
    craftingQueue: [],
};

/**
 * Holds all items that the player has in the game by id and amount.
 */
type InventoryContextType = [GlobalInventory, React.Dispatch<React.SetStateAction<GlobalInventory>>];
const InventoryContext = React.createContext<InventoryContextType | undefined>(undefined);

const normalizeGlobalInventory = (state: GlobalInventory | null): GlobalInventory => {
    const baseState = state ?? initialGlobalInventory;
    const inventory = ensureInventoryItems(baseState.inventory?.length ? baseState.inventory : StartingInventory);

    let minerState = (baseState.minerState ?? []).map(field => {
        const normalizedMiners = (field.miners ?? []).map(normalizeActiveItem);
        const clearedSpots = Math.min(
            10,
            Math.max(field.clearedSpots ?? normalizedMiners.length ?? 1, normalizedMiners.length, 1),
        );
        return {
            resourceType: field.resourceType,
            miners: normalizedMiners,
            clearedSpots,
            fuelType: field.fuelType,
        } as MinerState;
    });
    minerState = ensureField(minerState, COAL_MINER, () => initialMiners[0]);
    minerState = ensureField(minerState, IRON_MINER, () => initialMiners[1]);
    minerState = ensureField(minerState, COPPER_MINER, () => initialMiners[2]);
    minerState = ensureField(minerState, STONE_MINER, () => initialMiners[3]);

    let smelterState = (baseState.smelterState ?? []).map(field => {
        const normalizedSmelters = (field.smelters ?? []).map(normalizeActiveItem);
        const clearedSpots = Math.min(
            10,
            Math.max(field.clearedSpots ?? normalizedSmelters.length ?? 1, normalizedSmelters.length, 1),
        );
        return {
            resourceType: field.resourceType,
            smelters: normalizedSmelters,
            fuelType: field.fuelType ?? null,
            clearedSpots,
        };
    });
    smelterState = ensureField(smelterState, IRON_MINER, () => initialSmelters[0]);
    smelterState = ensureField(smelterState, "copper", () => initialSmelters[1]);
    smelterState = ensureField(smelterState, "stone", () => initialSmelters[2]);
    smelterState = ensureField(smelterState, "steel", () => initialSmelters[3]);

    const technologies = baseState.technologies ?? [];
    const powerMachines: PowerMachineState = {
        waterPumps: (baseState.powerMachines?.waterPumps ?? []).map(normalizeActiveItem),
        waterBoilers: (baseState.powerMachines?.waterBoilers ?? []).map(normalizeActiveItem),
        steamGenerators: (baseState.powerMachines?.steamGenerators ?? []).map(normalizeActiveItem),
    };
    if (powerMachines.waterPumps.length === 0) {
        powerMachines.waterPumps = [{itemId: "waterPump", running: true, progressMs: 0}];
    }
    (Object.keys(powerMachines) as PowerMachineType[]).forEach(key => {
        powerMachines[key] = powerMachines[key].slice(0, POWER_LIMITS[key]);
    });
    const powerStats = {...initialPowerStats, ...(baseState.powerStats ?? {})};
    const pollution = Math.max(0, baseState.pollution ?? 0);

    const assemblySetups = (baseState.assemblySetups ?? []).map(setup => ({
        ...setup,
        nodes: setup.nodes.map(node => ({
            ...node,
            running: node.running ?? true,
            overclocked: node.overclocked ?? false,
            progressMs: node.progressMs ?? 0,
        })),
    }));

    const craftingQueue = (baseState.craftingQueue ?? []).map(item => ({
        id: item.id,
        recipeId: item.recipeId,
        progressMs: item.progressMs ?? 0,
        missingIngredients: item.missingIngredients ?? false,
    }));

    return {
        inventory,
        minerState,
        smelterState,
        technologies,
        powerMachines,
        powerStats,
        pollution,
        assemblySetups,
        fuelAmountPerClick: baseState.fuelAmountPerClick ?? 1,
        craftingQueue,
    };
};

function useInventory() {
    const context = React.useContext(InventoryContext);
    if (!context) {
        throw new Error(`useInventory must be used within InventoryProvider`);
    }
    const [inventory, setInventory] = context;

    // Save inventory to local storage whenever it changes
    React.useEffect(() => {
        localStorage.setItem('inventory', JSON.stringify(inventory));
    }, [inventory]);

    const resetInventory = () => {
        localStorage.removeItem('inventory');
        localStorage.removeItem('miners');
        setInventory(initialGlobalInventory);
    };

    const updateInventoryItem = (itemId: string, quantity: number) => {
        if (quantity === 0) {
            return;
        }
        setInventory(prevInventory => {
            const newInventory = [...prevInventory.inventory];
            const existingItem = newInventory.find(item => item.item.id === itemId);
            if (existingItem === undefined) throw new Error("typeId not found in Inventory");
            const currentCount = existingItem.quantity ?? 0;
            if (currentCount + quantity < 0) throw new Error("Inventory Item quantity shouldn't go under 0 " + itemId);
            newInventory[newInventory.indexOf(existingItem)] = {...existingItem, quantity: currentCount + quantity};
            return {...prevInventory, inventory: newInventory};
        });
    };

    const getItemQuantity = (itemId: string) => {
        const currentItemQ = inventory.inventory.find(item => item.item.id === itemId);
        return currentItemQ?.quantity ?? 0;
    };

    const getItemsByType = (property: InventoryPropertiesEnum) => {
        return inventory.inventory.filter(item => item.item.properties.includes(property));
    };

    const hasTechnology = useCallback((technologyId: string) => {
        return (inventory.technologies ?? []).includes(technologyId);
    }, [inventory.technologies]);

    const addMiner = (field: string, itemId: string) => {
        setInventory(prevState => {
            const newMiners = prevState.minerState.map(fieldState => {
                if (fieldState.resourceType !== field) {
                    return fieldState;
                }
                const maxSlots = Math.min(10, fieldState.clearedSpots ?? 0);
                if (fieldState.miners.length >= maxSlots) {
                    return fieldState;
                }

                return {
                    ...fieldState,
                    miners: [...fieldState.miners, {itemId, running: false, progressMs: 0, fuelBuffer: 0}],
                };
            });

            return {...prevState, minerState: newMiners};
        });
    };

    const removeMiner = (field: string, minerIndex: number) => {
        setInventory(prevState => {
            const newInventory = [...prevState.inventory];
            const fieldState = prevState.minerState.find(f => f.resourceType === field);
            if (!fieldState || minerIndex >= fieldState.miners.length) {
                return prevState;
            }

            const miner = fieldState.miners[minerIndex];

            // Return the miner to inventory
            const minerInv = newInventory.find(inv => inv.item.id === miner.itemId);
            if (minerInv) {
                minerInv.quantity += 1;
            }

            // Recover unused fuel from the miner's buffer
            if (miner.fuelBuffer !== undefined && miner.fuelBuffer > 0) {
                const fuelType = fieldState.fuelType ?? "coal";
                const fuelToRecover = Math.floor(miner.fuelBuffer); // Round down to whole fuel units

                if (fuelToRecover > 0) {
                    const fuelInv = newInventory.find(inv => inv.item.id === fuelType);
                    if (fuelInv) {
                        fuelInv.quantity += fuelToRecover;
                    }
                }
            }

            const newMiners = prevState.minerState.map(f => {
                if (f.resourceType !== field) return f;
                return {
                    ...f,
                    miners: f.miners.filter((_, idx) => idx !== minerIndex),
                };
            });

            return {...prevState, minerState: newMiners, inventory: newInventory};
        });
    };

    const removeSmelter = (field: string, smelterIndex: number) => {
        setInventory(prevState => {
            const newInventory = [...prevState.inventory];
            const fieldState = prevState.smelterState.find(f => f.resourceType === field);
            if (!fieldState || smelterIndex >= fieldState.smelters.length) {
                return prevState;
            }

            const smelter = fieldState.smelters[smelterIndex];

            // Return the smelter to inventory
            const smelterInv = newInventory.find(inv => inv.item.id === smelter.itemId);
            if (smelterInv) {
                smelterInv.quantity += 1;
            }

            // Recover unused fuel from the smelter's buffer
            if (smelter.fuelBuffer !== undefined && smelter.fuelBuffer > 0) {
                const fuelType = fieldState.fuelType ?? "coal";
                const fuelToRecover = Math.floor(smelter.fuelBuffer); // Round down to whole fuel units

                if (fuelToRecover > 0) {
                    const fuelInv = newInventory.find(inv => inv.item.id === fuelType);
                    if (fuelInv) {
                        fuelInv.quantity += fuelToRecover;
                    }
                }
            }

            const newSmelters = prevState.smelterState.map(f => {
                if (f.resourceType !== field) return f;
                return {
                    ...f,
                    smelters: f.smelters.filter((_, idx) => idx !== smelterIndex),
                };
            });

            return {...prevState, smelterState: newSmelters, inventory: newInventory};
        });
    };

    const replaceMiner = (field: string, minerIndex: number, newItemId: string) => {
        setInventory(prevState => {
            const newInventory = [...prevState.inventory];
            const fieldState = prevState.minerState.find(f => f.resourceType === field);
            if (!fieldState || minerIndex >= fieldState.miners.length) {
                return prevState;
            }

            const oldMiner = fieldState.miners[minerIndex];
            const newMinerInv = newInventory.find(inv => inv.item.id === newItemId);
            if (!newMinerInv || newMinerInv.quantity < 1) {
                return prevState; // Don't have the new miner
            }

            // Return old miner to inventory
            const oldMinerInv = newInventory.find(inv => inv.item.id === oldMiner.itemId);
            if (oldMinerInv) {
                oldMinerInv.quantity += 1;
            }

            // Remove new miner from inventory
            newMinerInv.quantity -= 1;

            const newMiners = prevState.minerState.map(f => {
                if (f.resourceType !== field) return f;
                return {
                    ...f,
                    miners: f.miners.map((m, idx) =>
                        idx === minerIndex
                            ? {itemId: newItemId, running: true, progressMs: 0, fuelBuffer: 0}
                            : m
                    ),
                };
            });

            return {...prevState, minerState: newMiners, inventory: newInventory};
        });
    };

    const toggleMiner = (field: string, index: number) => {
        setInventory(prevState => {
            const hasAutoFueling = (prevState.technologies ?? []).includes("tech-auto-miner-fueling");
            let updatedInventory = [...prevState.inventory];

            const newMiners = prevState.minerState.map(fieldState => {
                if (fieldState.resourceType !== field) {
                    return fieldState;
                }

                const fuelType = fieldState.fuelType ?? "coal";

                return {
                    ...fieldState,
                    miners: fieldState.miners.map((miner, minerIndex) => {
                        if (minerIndex !== index) return miner;

                        const behavior = MINER_BEHAVIORS[miner.itemId];
                        // Only check fuel for burner miners (those with fuelCostPerCycle)
                        // Electric miners don't need fuel
                        if (!miner.running && behavior?.fuelCostPerCycle && miner.fuelBuffer !== undefined) {
                            // Calculate minimum fuel for one cycle (0.1 MJ per ore * outputPerCycle)
                            const minFuelForOneCycle = 0.1 * behavior.outputPerCycle;
                            if (miner.fuelBuffer < minFuelForOneCycle) {
                                // If auto-fueling is enabled, add 1 fuel and allow turning on
                                if (hasAutoFueling) {
                                    const fuelInventory = updatedInventory.find(inv => inv.item.id === fuelType);
                                    if (fuelInventory && fuelInventory.quantity >= 1) {
                                        fuelInventory.quantity -= 1;
                                        return {
                                            ...miner,
                                            running: true,
                                            fuelBuffer: (miner.fuelBuffer ?? 0) + 1,
                                        };
                                    }
                                }
                                // Don't allow turning on without sufficient fuel
                                return miner;
                            }
                        }

                        return {
                            ...miner,
                            running: !miner.running,
                        };
                    }),
                };
            });

            return {...prevState, minerState: newMiners, inventory: updatedInventory};
        });
    };

    const completePatchUnlock = (resourceType: string, rewards: ItemQuantity[]) => {
        setInventory(prevState => {
            const updatedInventory = ensureInventoryItems(prevState.inventory).map(entry => ({...entry}));
            rewards.forEach(reward => {
                const target = updatedInventory.find(item => item.item.id === reward.itemId);
                if (target) {
                    target.quantity += reward.quantity;
                }
            });
            const newMiners = prevState.minerState.map(field => {
                if (field.resourceType !== resourceType) {
                    return field;
                }
                const clearedSpots = Math.min(10, Math.max(field.clearedSpots ?? field.miners.length, field.miners.length) + 1);
                return {...field, clearedSpots};
            });
            return {...prevState, inventory: updatedInventory, minerState: newMiners};
        });
    };

    const completeSmelterUnlock = (resourceType: string, rewards: ItemQuantity[]) => {
        setInventory(prevState => {
            const updatedInventory = ensureInventoryItems(prevState.inventory).map(entry => ({...entry}));
            rewards.forEach(reward => {
                const target = updatedInventory.find(item => item.item.id === reward.itemId);
                if (target) {
                    target.quantity += reward.quantity;
                }
            });
            const newSmelters = prevState.smelterState.map(field => {
                if (field.resourceType !== resourceType) {
                    return field;
                }
                const clearedSpots = Math.min(10, Math.max(field.clearedSpots ?? field.smelters.length, field.smelters.length) + 1);
                return {...field, clearedSpots};
            });
            return {...prevState, inventory: updatedInventory, smelterState: newSmelters};
        });
    };

    const addPollution = (amount: number) => {
        if (amount === 0) {
            return;
        }
        setInventory(prevState => ({
            ...prevState,
            pollution: accumulatePollution(prevState.pollution, amount),
        }));
    };


    const addSmelter = (field: string, itemId: string) => {
        setInventory(prevState => {
            const smelterState = prevState.smelterState.map(fieldState => {
                if (fieldState.resourceType !== field) {
                    return fieldState;
                }
                const maxSlots = Math.min(10, fieldState.clearedSpots ?? 0);
                if (fieldState.smelters.length >= maxSlots) {
                    return fieldState;
                }

                return {
                    ...fieldState,
                    smelters: [...fieldState.smelters, {itemId, running: false, progressMs: 0, fuelBuffer: 0}],
                };
            });

            return {...prevState, smelterState};
        });
    };

    const toggleSmelter = (field: string, index: number) => {
        setInventory(prevState => {
            const hasAutoFueling = (prevState.technologies ?? []).includes("tech-auto-smelter-fueling");
            let updatedInventory = [...prevState.inventory];

            const smelterState = prevState.smelterState.map(fieldState => {
                if (fieldState.resourceType !== field) {
                    return fieldState;
                }

                return {
                    ...fieldState,
                    smelters: fieldState.smelters.map((smelter, smelterIndex) => {
                        if (smelterIndex !== index) return smelter;

                        // If trying to turn on, check if we have enough fuel for one cycle
                        if (!smelter.running && smelter.fuelBuffer !== undefined) {
                            // Calculate minimum fuel based on smelter type
                            let minFuelForOneCycle = 0;
                            switch (field) {
                                case "iron":
                                    minFuelForOneCycle = ENERGY_COSTS.smeltingIron; // 0.2 MJ
                                    break;
                                case "copper":
                                    minFuelForOneCycle = ENERGY_COSTS.smeltingCopper; // 0.2 MJ
                                    break;
                                case "stone":
                                    minFuelForOneCycle = ENERGY_COSTS.smeltingStone; // 0.4 MJ
                                    break;
                                case "steel":
                                    minFuelForOneCycle = ENERGY_COSTS.smeltingSteel; // 0.8 MJ
                                    break;
                            }

                            if (smelter.fuelBuffer < minFuelForOneCycle) {
                                // If auto-fueling is enabled, add 1 fuel and allow turning on
                                if (hasAutoFueling) {
                                    const fuelType = fieldState.fuelType ?? "coal";
                                    const fuelInventory = updatedInventory.find(inv => inv.item.id === fuelType);
                                    if (fuelInventory && fuelInventory.quantity >= 1) {
                                        fuelInventory.quantity -= 1;
                                        return {
                                            ...smelter,
                                            running: true,
                                            fuelBuffer: (smelter.fuelBuffer ?? 0) + 1,
                                        };
                                    }
                                }
                                // Don't allow turning on without sufficient fuel
                                return smelter;
                            }
                        }

                        return {
                            ...smelter,
                            running: !smelter.running,
                        };
                    }),
                };
            });

            return {...prevState, smelterState, inventory: updatedInventory};
        });
    };

    const updatePowerMachineList = (machineType: PowerMachineType, updater: (list: ActiveItem[]) => ActiveItem[]) => {
        setInventory(prevState => {
            const currentList = [...(prevState.powerMachines[machineType] ?? [])];
            const updatedList = updater(currentList);
            const powerMachines = {...prevState.powerMachines, [machineType]: updatedList};
            return {...prevState, powerMachines};
        });
    };

    const addPowerMachine = (machineType: PowerMachineType, itemId: string) => {
        updatePowerMachineList(machineType, (list) => list.length >= POWER_LIMITS[machineType]
            ? list
            : [...list, {itemId, running: machineType === "waterPumps", progressMs: 0, fuelStarved: false}]);
    };

    const togglePowerMachine = (machineType: PowerMachineType, index: number) => {
        updatePowerMachineList(machineType, (list) => list.map((machine, idx) => idx === index ? {
            ...machine,
            running: !machine.running,
            fuelStarved: false,
        } : machine));
    };

    const addFuelToSmelter = (field: string, fuelType: string) => {
        setInventory(prevState => {
            const newSmelters = [...prevState.smelterState];
            const currentField = newSmelters.find((it) => it.resourceType === field);
            if (!currentField) throw new Error(`Couldn't find smelter field: ${field}`);
            currentField.fuelType = fuelType;
            return {...prevState, smelterState: newSmelters};
        });
    };

    const addFuelToMiner = (field: string, fuelType: string) => {
        setInventory(prevState => {
            const newMiners = [...prevState.minerState];
            const currentField = newMiners.find((it) => it.resourceType === field);
            if (!currentField) throw new Error(`Couldn't find miner field: ${field}`);
            currentField.fuelType = fuelType;
            return {...prevState, minerState: newMiners};
        });
    };

    // Manual fueling for miners
    const addFuelToMinerBuffer = (field: string, minerIndex: number, fuelItemId: string = "coal") => {
        const fuelAmount = inventory.fuelAmountPerClick ?? 1;
        const fuelEnergy = FUEL_ENERGY_VALUES[fuelItemId] ?? 1;

        setInventory(prevState => {
            const coalInventory = prevState.inventory.find(inv => inv.item.id === fuelItemId);
            if (!coalInventory || coalInventory.quantity < fuelAmount) {
                return prevState; // Not enough fuel
            }

            const newMiners = prevState.minerState.map(fieldState => {
                if (fieldState.resourceType !== field) return fieldState;

                const newMinersList = fieldState.miners.map((miner, idx) => {
                    if (idx !== minerIndex) return miner;

                    const currentFuel = miner.fuelBuffer ?? 0;
                    if (currentFuel >= FUEL_BUFFER_CAPACITY) return miner; // Buffer full

                    const spaceInBuffer = FUEL_BUFFER_CAPACITY - currentFuel;
                    const actualFuelToAdd = Math.min(fuelAmount, spaceInBuffer);

                    return {
                        ...miner,
                        fuelBuffer: currentFuel + actualFuelToAdd,
                    };
                });

                return {...fieldState, miners: newMinersList};
            });

            const newInventory = prevState.inventory.map(inv =>
                inv.item.id === fuelItemId
                    ? {...inv, quantity: inv.quantity - fuelAmount}
                    : inv
            );

            return {...prevState, minerState: newMiners, inventory: newInventory};
        });
    };

    // Manual fueling for smelters
    const addFuelToSmelterBuffer = (field: string, smelterIndex: number, fuelItemId: string = "coal") => {
        const fuelAmount = inventory.fuelAmountPerClick ?? 1;
        const fuelEnergy = FUEL_ENERGY_VALUES[fuelItemId] ?? 1;

        setInventory(prevState => {
            const coalInventory = prevState.inventory.find(inv => inv.item.id === fuelItemId);
            if (!coalInventory || coalInventory.quantity < fuelAmount) {
                return prevState; // Not enough fuel
            }

            const newSmelters = prevState.smelterState.map(fieldState => {
                if (fieldState.resourceType !== field) return fieldState;

                const newSmeltersList = fieldState.smelters.map((smelter, idx) => {
                    if (idx !== smelterIndex) return smelter;

                    const currentFuel = smelter.fuelBuffer ?? 0;
                    if (currentFuel >= FUEL_BUFFER_CAPACITY) return smelter; // Buffer full

                    const spaceInBuffer = FUEL_BUFFER_CAPACITY - currentFuel;
                    const actualFuelToAdd = Math.min(fuelAmount, spaceInBuffer);

                    return {
                        ...smelter,
                        fuelBuffer: currentFuel + actualFuelToAdd,
                    };
                });

                return {...fieldState, smelters: newSmeltersList};
            });

            const newInventory = prevState.inventory.map(inv =>
                inv.item.id === fuelItemId
                    ? {...inv, quantity: inv.quantity - fuelAmount}
                    : inv
            );

            return {...prevState, smelterState: newSmelters, inventory: newInventory};
        });
    };

    // Set fuel amount per click
    const setFuelAmountPerClick = (amount: number) => {
        setInventory(prevState => ({
            ...prevState,
            fuelAmountPerClick: Math.max(1, Math.min(10, amount)), // Clamp between 1-10
        }));
    };

    const canCraft = (recipeId: string) => {
        const recipe = CRAFTING_RECIPES.find(it => it.id === recipeId);
        if (!recipe) {
            return false;
        }
        return recipe.ingredients.every(ingredient => getItemQuantity(ingredient.itemId) >= ingredient.quantity);
    };

    const craftItem = (recipeId: string) => {
        const recipe = CRAFTING_RECIPES.find(it => it.id === recipeId);
        if (!recipe) {
            return false;
        }
        let crafted = false;
        setInventory(prevState => {
            const normalizedInventory = ensureInventoryItems(prevState.inventory);
            const inventoryCopy = normalizedInventory.map(entry => ({...entry}));
            const hasAllIngredients = recipe.ingredients.every(ingredient => {
                const entry = inventoryCopy.find(item => item.item.id === ingredient.itemId);
                return (entry?.quantity ?? 0) >= ingredient.quantity;
            });
            if (!hasAllIngredients) {
                return prevState;
            }
            recipe.ingredients.forEach(ingredient => {
                const entry = inventoryCopy.find(item => item.item.id === ingredient.itemId);
                if (entry) {
                    entry.quantity -= ingredient.quantity;
                }
            });
            const resultEntry = inventoryCopy.find(item => item.item.id === recipe.result.itemId);
            if (!resultEntry) {
                const newItem = ItemList.find(it => it.id === recipe.result.itemId);
                if (!newItem) {
                    throw new Error(`Unknown craft result: ${recipe.result.itemId}`);
                }
                inventoryCopy.push({item: newItem, quantity: recipe.result.quantity});
            } else {
                resultEntry.quantity += recipe.result.quantity;
            }
            crafted = true;
            return {...prevState, inventory: inventoryCopy};
        });
        return crafted;
    };

    const purchaseTechnology = (technologyId: string, options?: {ignoreCost?: boolean, ignorePrerequisites?: boolean}) => {
        const technology = TECHNOLOGIES[technologyId];
        if (!technology || hasTechnology(technologyId)) {
            return false;
        }
        const prerequisitesMet = (technology.prerequisites ?? []).every(req => hasTechnology(req));
        if (!prerequisitesMet && !options?.ignorePrerequisites) {
            return false;
        }
        let bought = false;
        setInventory(prevState => {
            const normalizedInventory = ensureInventoryItems(prevState.inventory);
            const inventoryCopy = normalizedInventory.map(entry => ({...entry}));
            if (!options?.ignoreCost) {
                const affordable = technology.cost.every(cost => {
                    const entry = inventoryCopy.find(item => item.item.id === cost.itemId);
                    return (entry?.quantity ?? 0) >= cost.quantity;
                });
                if (!affordable) {
                    return prevState;
                }
                technology.cost.forEach(cost => {
                    const entry = inventoryCopy.find(item => item.item.id === cost.itemId);
                    if (entry) {
                        entry.quantity -= cost.quantity;
                    }
                });
            }
            bought = true;

            // Handle special technology unlocks
            const newState = {
                ...prevState,
                inventory: inventoryCopy,
                technologies: [...(prevState.technologies ?? []), technologyId],
            };

            // Special handling for advanced steel - modify smelter recipe
            if (technologyId === 'tech-advanced-steel') {
                // Steel recipe is modified to require 4 iron plates instead of 5
                // This is handled in the smelter component by checking for this tech
            }

            return newState;
        });
        return bought;
    };

    const updateAssemblySetup = (setupId: string, updater: (setup: AssemblySetup) => AssemblySetup) => {
        setInventory(prevState => ({
            ...prevState,
            assemblySetups: prevState.assemblySetups.map(setup => setup.id === setupId ? updater(setup) : setup),
        }));
    };

    const addAssemblySetup = (setup: AssemblySetup) => {
        setInventory(prevState => ({
            ...prevState,
            assemblySetups: [...prevState.assemblySetups, setup],
        }));
    };

    const deleteAssemblySetup = (setupId: string) => {
        setInventory(prevState => ({
            ...prevState,
            assemblySetups: prevState.assemblySetups.filter(s => s.id !== setupId),
        }));
    };

    const addToQueue = useCallback((recipeId: string) => {
        setInventory(prevState => ({
            ...prevState,
            craftingQueue: [
                ...prevState.craftingQueue,
                {
                    id: `craft-${Date.now()}-${Math.random()}`,
                    recipeId,
                    progressMs: 0,
                    missingIngredients: false,
                }
            ]
        }));
    }, []);

    const removeFromQueue = useCallback((itemId: string) => {
        setInventory(prevState => ({
            ...prevState,
            craftingQueue: prevState.craftingQueue.filter(item => item.id !== itemId)
        }));
    }, []);

    const reorderQueue = useCallback((fromIndex: number, toIndex: number) => {
        setInventory(prevState => {
            const newQueue = [...prevState.craftingQueue];
            const [movedItem] = newQueue.splice(fromIndex, 1);
            newQueue.splice(toIndex, 0, movedItem);
            return {...prevState, craftingQueue: newQueue};
        });
    }, []);

    const queueMissingIngredients = useCallback((queueItemId: string, quantity: number = 1) => {
        setInventory(prevState => {
            const queueIndex = prevState.craftingQueue.findIndex(item => item.id === queueItemId);
            if (queueIndex === -1) return prevState;

            const queueItem = prevState.craftingQueue[queueIndex];
            const recipe = CRAFTING_RECIPES.find(r => r.id === queueItem.recipeId);
            if (!recipe) return prevState;

            // Find missing ingredient recipes for the entire stack
            const newQueueItems: CraftingQueueItem[] = [];
            for (const ing of recipe.ingredients) {
                const owned = getItemQuantity(ing.itemId);
                const totalNeeded = ing.quantity * quantity; // Multiply by stack size
                if (owned < totalNeeded) {
                    const deficit = totalNeeded - owned;
                    const ingredientRecipe = CRAFTING_RECIPES.find(r => r.result.itemId === ing.itemId);
                    if (ingredientRecipe) {
                        // Calculate how many times we need to craft this recipe
                        const recipesNeeded = Math.ceil(deficit / ingredientRecipe.result.quantity);

                        // Queue that many recipes
                        for (let i = 0; i < recipesNeeded; i++) {
                            newQueueItems.push({
                                id: `craft-${Date.now()}-${Math.random()}-${i}`,
                                recipeId: ingredientRecipe.id,
                                progressMs: 0,
                                missingIngredients: false,
                            });
                        }
                    }
                }
            }

            // Insert before the current item
            const updatedQueue = [
                ...prevState.craftingQueue.slice(0, queueIndex),
                ...newQueueItems,
                ...prevState.craftingQueue.slice(queueIndex)
            ];

            return {...prevState, craftingQueue: updatedQueue};
        });
    }, [getItemQuantity]);

    return {
        inventory,
        updateInventoryItem,
        getItemQuantity,
        resetInventory,
        toggleMiner,
        addMiner,
        removeMiner,
        replaceMiner,
        completePatchUnlock,
        completeSmelterUnlock,
        toggleSmelter,
        addSmelter,
        removeSmelter,
        addFuelToSmelter,
        addFuelToMiner,
        addFuelToMinerBuffer,
        addFuelToSmelterBuffer,
        setFuelAmountPerClick,
        getItemsByType,
        craftItem,
        canCraft,
        hasTechnology,
        purchaseTechnology,
        addPowerMachine,
        togglePowerMachine,
        pollution: inventory.pollution,
        addPollution,
        setExternalPowerDemand: (demand: number) => {
            setInventory(prevState => ({
                ...prevState,
                powerStats: {
                    ...prevState.powerStats,
                    externalDemand: Math.max(0, demand),
                },
            }));
        },
        updateAssemblySetup,
        addAssemblySetup,
        deleteAssemblySetup,
        addToQueue,
        removeFromQueue,
        reorderQueue,
        queueMissingIngredients,
    };
}

// Assembly helper functions
const nodeHasIngredients = (setup: AssemblySetup, node: AssemblyNode): boolean => {
    if (node.type !== "assembler" || !node.recipeId) return false;
    const recipe = ASSEMBLY_RECIPES[node.recipeId];
    if (!recipe) return false;
    const incoming = setup.connections.filter(conn => conn.toId === node.id);
    return recipe.ingredients.every(ingredient => incoming.some(conn => conn.itemId === ingredient.itemId));
};

// Calculate how much a source node can provide this tick (in items, not items/sec)
const calculateSourceProvision = (
    sourceNode: AssemblyNode,
    allSetups: AssemblySetup[],
    activeSetupId: string,
    smelterState: SmelterState[]
): number => {
    if (sourceNode.type !== "source" || !sourceNode.itemId) return 0;

    const perNodeOutputPerSecond = calculateSourceNodeOutputPerSecond(
        smelterState,
        allSetups,
        activeSetupId,
        sourceNode.itemId
    );

    // Convert to provision per tick
    const secondsPerTick = TIME_INCREMENT_MS / 1000;
    return perNodeOutputPerSecond * secondsPerTick;
};

function InventoryProvider({children}: { children: React.ReactNode }) {
    const [inventory, setInventory] = React.useState<GlobalInventory>(() => {
        const storedInventory = localStorage.getItem("inventory");
        return normalizeGlobalInventory(storedInventory ? JSON.parse(storedInventory) : initialGlobalInventory);
    });

    const processTick = useCallback(() => {
        setInventory((prevState) => {
            const normalized = normalizeGlobalInventory(prevState);
            const newInventory = [...normalized.inventory];
            const newMinerState = normalized.minerState.map(field => ({
                resourceType: field.resourceType,
                miners: field.miners.map(normalizeActiveItem),
                clearedSpots: field.clearedSpots ?? Math.max(field.miners.length, 0),
                fuelType: field.fuelType,
            }));
            const newSmelterState = normalized.smelterState.map(field => ({
                resourceType: field.resourceType,
                smelters: field.smelters.map(normalizeActiveItem),
                fuelType: field.fuelType,
                clearedSpots: field.clearedSpots ?? Math.max(field.smelters.length, 0),
            }));
            const newPowerMachines: PowerMachineState = {
                waterPumps: normalized.powerMachines.waterPumps.map(normalizeActiveItem),
                waterBoilers: normalized.powerMachines.waterBoilers.map(normalizeActiveItem),
                steamGenerators: normalized.powerMachines.steamGenerators.map(normalizeActiveItem),
            };
            let pollutionDelta = 0;

            const secondsPerTick = TIME_INCREMENT_MS / 1000;
            const boilerWaterPerSecond = POWER_CONSTANTS.boilerWaterPerCycle / (POWER_CONSTANTS.boilerCycleMs / 1000);
            const boilerSteamPerSecond = POWER_CONSTANTS.boilerSteamPerCycle / (POWER_CONSTANTS.boilerCycleMs / 1000);
            const boilerFuelPerTick = (POWER_CONSTANTS.boilerFuelCost.quantity * TIME_INCREMENT_MS) / POWER_CONSTANTS.boilerCycleMs;
            const baseSteamPerBoiler = boilerSteamPerSecond * secondsPerTick;
            const steamPerWater = boilerSteamPerSecond / boilerWaterPerSecond;

            const pumpsRunning = newPowerMachines.waterPumps.filter(pump => pump.running).length;
            const waterProduced = pumpsRunning * POWER_CONSTANTS.waterPerPumpPerSecond;
            const waterSteamLimit = waterProduced * steamPerWater;
            let availableWater = waterProduced;
            let waterUsed = 0;

            const generatorsRunning = newPowerMachines.steamGenerators.filter(generator => generator.running).length;
            const steamDemand = generatorsRunning * POWER_CONSTANTS.generatorSteamPerSecond;
            let remainingSteamDemand = steamDemand;
            let steamProduced = 0;
            const runningBoilers = newPowerMachines.waterBoilers.filter(boiler => boiler.running).length;
            const steamCapacity = Math.min(runningBoilers * baseSteamPerBoiler, waterSteamLimit);
            let fuelUsed = 0;
            pollutionDelta += generatorsRunning * POLLUTION_IMPACT.generatorTick;

            newPowerMachines.waterBoilers = newPowerMachines.waterBoilers.map(boiler => {
                const boilerWithFuelFlag = {...boiler};
                if (!boiler.running) {
                    return {...boilerWithFuelFlag, progressMs: 0};
                }
                if (remainingSteamDemand <= 0 || availableWater <= 0) {
                    return {...boilerWithFuelFlag, progressMs: 0};
                }
                const fuelInventory = findInventoryQuantity(newInventory, POWER_CONSTANTS.boilerFuelCost.itemId);
                if (!fuelInventory || fuelInventory.quantity <= 0) {
                    return {...boilerWithFuelFlag, running: false, progressMs: 0, fuelStarved: true};
                }

                const maxSteamFromWater = availableWater * steamPerWater;
                const desiredSteam = Math.min(baseSteamPerBoiler, remainingSteamDemand, maxSteamFromWater);
                if (desiredSteam <= 0) {
                    return {...boilerWithFuelFlag, progressMs: boiler.progressMs};
                }
                const runFraction = baseSteamPerBoiler === 0 ? 0 : desiredSteam / baseSteamPerBoiler;
                const fuelDebt = boiler.progressMs + (boilerFuelPerTick * runFraction);
                const wholeFuel = Math.floor(fuelDebt);
                const remainingFuelDebt = fuelDebt - wholeFuel;
                if (wholeFuel > 0 && fuelInventory.quantity < wholeFuel) {
                    return {...boilerWithFuelFlag, running: false, progressMs: 0, fuelStarved: true};
                }
                if (wholeFuel > 0) {
                    fuelInventory.quantity -= wholeFuel;
                    fuelUsed += wholeFuel;
                    pollutionDelta += wholeFuel * POLLUTION_IMPACT.fuelUnit;
                }

                const waterNeeded = desiredSteam / steamPerWater;
                availableWater -= waterNeeded;
                waterUsed += waterNeeded;
                steamProduced += desiredSteam;
                remainingSteamDemand = Math.max(0, remainingSteamDemand - desiredSteam);
                return {...boilerWithFuelFlag, progressMs: remainingFuelDebt, fuelStarved: false};
            });

            newPowerMachines.waterPumps = newPowerMachines.waterPumps.map(pump => pump.running ? {
                ...pump,
                progressMs: (pump.progressMs + TIME_INCREMENT_MS) % 1000,
            } : {...pump, progressMs: 0});
            newPowerMachines.steamGenerators = newPowerMachines.steamGenerators.map(generator => generator.running ? {
                ...generator,
                progressMs: (generator.progressMs + TIME_INCREMENT_MS) % 1000,
            } : {...generator, progressMs: 0});

            const steamUsed = Math.min(steamProduced, steamDemand);
            const powerSupply = steamUsed;
            const minerDemand = newMinerState.reduce((sum, field) => sum + field.miners.reduce((fieldSum, miner) => {
                if (!miner.running) {
                    return fieldSum;
                }
                const powerUse = GameItems[miner.itemId]?.powerUse ?? 0;
                return fieldSum + powerUse;
            }, 0), 0);

            // Calculate smelter power demand (electric furnaces only)
            const smelterDemand = newSmelterState.reduce((sum, field) => sum + field.smelters.reduce((fieldSum, smelter) => {
                if (!smelter.running || smelter.itemId !== "electricFurnace") {
                    return fieldSum;
                }
                const powerUse = GameItems[smelter.itemId]?.powerUse ?? 0;
                return fieldSum + powerUse;
            }, 0), 0);

            const externalDemand = normalized.powerStats.externalDemand ?? 0;
            const totalPowerDemand = minerDemand + smelterDemand + externalDemand;

            const powerRatio = totalPowerDemand === 0 ? 1 : Math.min(1, powerSupply / totalPowerDemand);

            newMinerState.forEach((field) => {
                field.miners.forEach((miner, minerIndex) => {
                    if (!miner.running) {
                        return;
                    }
                    const behavior = MINER_BEHAVIORS[miner.itemId];
                    if (!behavior || !behavior.supportedResources.includes(field.resourceType)) {
                        return;
                    }

                    const updatedMiner = {...miner};
                    const requiresPower = (GameItems[miner.itemId]?.powerUse ?? 0) > 0;
                    const progressIncrement = TIME_INCREMENT_MS * (requiresPower ? powerRatio : 1);
                    updatedMiner.progressMs += progressIncrement;

                    const resource = findInventoryQuantity(newInventory, field.resourceType);
                    if (!resource) throw new Error("invalid item?!");

                    const cyclesReady = calculateCompleteCycles(updatedMiner.progressMs, behavior.cycleTimeMs);
                    if (cyclesReady <= 0) {
                        newMinerState.find(m => m.resourceType === field.resourceType)!.miners[minerIndex] = updatedMiner;
                        return;
                    }

                    // Energy-based fuel system
                    const needsFuel = behavior.fuelCostPerCycle !== undefined;
                    let possibleCycles = cyclesReady;

                    if (needsFuel) {
                        // Calculate energy needed (0.1 MJ per ore * outputPerCycle * cyclesReady)
                        const energyPerCycle = ENERGY_COSTS.mining * behavior.outputPerCycle;
                        const totalEnergyNeeded = energyPerCycle * cyclesReady;

                        // Auto-refuel if technology is unlocked
                        const hasAutoFueling = (normalized.technologies ?? []).includes("tech-auto-miner-fueling");
                        const fuelType = field.fuelType ?? "coal";
                        const fuelEnergyValue = FUEL_ENERGY_VALUES[fuelType] ?? 1;

                        if (hasAutoFueling && updatedMiner.fuelBuffer !== undefined) {
                            // Round down fuel buffer to integer and recover fractional fuel
                            const fractionalFuel = updatedMiner.fuelBuffer - Math.floor(updatedMiner.fuelBuffer);
                            if (fractionalFuel > 0) {
                                const fuelInventory = findInventoryQuantity(newInventory, fuelType);
                                if (fuelInventory) {
                                    fuelInventory.quantity += fractionalFuel;
                                    updatedMiner.fuelBuffer = Math.floor(updatedMiner.fuelBuffer);
                                }
                            }

                            // Only add 1 fuel at a time when about to run out
                            const currentEnergy = updatedMiner.fuelBuffer * fuelEnergyValue;
                            if (currentEnergy < energyPerCycle) {
                                const fuelInventory = findInventoryQuantity(newInventory, fuelType);
                                if (fuelInventory && fuelInventory.quantity >= 1) {
                                    fuelInventory.quantity -= 1;
                                    updatedMiner.fuelBuffer += 1;
                                }
                            }
                        }

                        // Check fuel buffer using selected fuel type
                        const availableEnergy = (updatedMiner.fuelBuffer ?? 0) * fuelEnergyValue;

                        // Shutdown if not enough energy for even one cycle
                        if (availableEnergy < energyPerCycle) {
                            newMinerState.find(m => m.resourceType === field.resourceType)!.miners[minerIndex] = {
                                ...updatedMiner,
                                running: false,
                                progressMs: 0,
                                fuelStarved: true,
                            };
                            return;
                        }

                        // Reduce cycles if not enough energy for all ready cycles
                        possibleCycles = Math.min(cyclesReady, Math.floor(availableEnergy / energyPerCycle));

                        // Consume fuel from buffer
                        const energyConsumed = possibleCycles * energyPerCycle;
                        const fuelConsumed = energyConsumed / fuelEnergyValue;
                        updatedMiner.fuelBuffer = (updatedMiner.fuelBuffer ?? 0) - fuelConsumed;
                        pollutionDelta += fuelConsumed * POLLUTION_IMPACT.fuelUnit;
                    }

                    produceOutput(resource, behavior.outputPerCycle, possibleCycles);
                    pollutionDelta += possibleCycles * POLLUTION_IMPACT.minerCycle;
                    updatedMiner.progressMs = calculateRemainingProgress(updatedMiner.progressMs, behavior.cycleTimeMs);
                    newMinerState.find(m => m.resourceType === field.resourceType)!.miners[minerIndex] = updatedMiner;
                });
            });

            newSmelterState.forEach((smelterState) => {
                const recipe = SMELTER_RECIPES[smelterState.resourceType];
                if (!recipe) {
                    return;
                }
                smelterState.smelters.forEach((smelter, smelterIndex) => {
                    if (!smelter.running) {
                        return;
                    }
                    const updatedSmelter = {...smelter};
                    const requiresPower = (GameItems[smelter.itemId]?.powerUse ?? 0) > 0;
                    const progressIncrement = TIME_INCREMENT_MS * (requiresPower ? powerRatio : 1);
                    updatedSmelter.progressMs += progressIncrement;

                    const inputResource = findInventoryQuantity(newInventory, recipe.inputId);
                    const outputResource = findInventoryQuantity(newInventory, recipe.outputId);
                    if (!inputResource || !outputResource) throw new Error("invalid item?!");

                    const cyclesReady = calculateCompleteCycles(updatedSmelter.progressMs, recipe.cycleTimeMs);
                    if (cyclesReady <= 0) {
                        newSmelterState.find(s => s.resourceType === smelterState.resourceType)!.smelters[smelterIndex] = updatedSmelter;
                        return;
                    }

                    // Adjust input quantity for advanced steel technology
                    let inputQuantity = recipe.inputQuantity;
                    if (recipe.id === "smelt-steel" && (normalized.technologies ?? []).includes("tech-advanced-steel")) {
                        inputQuantity = 4; // Advanced steel production: 4 iron plates instead of 5
                    }

                    // Energy-based fuel system for smelters
                    const isElectricFurnace = smelter.itemId === "electricFurnace";
                    const needsFuel = recipe.fuelCostPerCycle !== undefined && !isElectricFurnace;
                    let possibleCycles = cyclesReady;

                    if (needsFuel) {
                        // Determine energy cost based on recipe
                        let energyPerOutput = ENERGY_COSTS.smeltingIron; // default
                        if (recipe.id === "smelt-copper") energyPerOutput = ENERGY_COSTS.smeltingCopper;
                        else if (recipe.id === "smelt-stone-brick") energyPerOutput = ENERGY_COSTS.smeltingStone;
                        else if (recipe.id === "smelt-steel") energyPerOutput = ENERGY_COSTS.smeltingSteel;

                        const energyPerCycle = energyPerOutput * recipe.outputQuantity;

                        // Auto-refuel if technology is unlocked
                        const hasAutoFueling = (normalized.technologies ?? []).includes("tech-auto-smelter-fueling");
                        if (hasAutoFueling && updatedSmelter.fuelBuffer !== undefined) {
                            // Round down fuel buffer to integer and recover fractional fuel
                            const fractionalFuel = updatedSmelter.fuelBuffer - Math.floor(updatedSmelter.fuelBuffer);
                            if (fractionalFuel > 0) {
                                const coalInventory = findInventoryQuantity(newInventory, "coal");
                                if (coalInventory) {
                                    coalInventory.quantity += fractionalFuel;
                                    updatedSmelter.fuelBuffer = Math.floor(updatedSmelter.fuelBuffer);
                                }
                            }

                            // Only add 1 coal at a time when about to run out
                            const currentEnergy = updatedSmelter.fuelBuffer * FUEL_ENERGY_VALUES.coal;
                            if (currentEnergy < energyPerCycle) {
                                const coalInventory = findInventoryQuantity(newInventory, "coal");
                                if (coalInventory && coalInventory.quantity >= 1) {
                                    coalInventory.quantity -= 1;
                                    updatedSmelter.fuelBuffer += 1;
                                }
                            }
                        }

                        const availableEnergy = (updatedSmelter.fuelBuffer ?? 0) * FUEL_ENERGY_VALUES.coal;

                        // Shutdown if not enough energy for even one cycle
                        if (availableEnergy < energyPerCycle) {
                            newSmelterState.find(s => s.resourceType === smelterState.resourceType)!.smelters[smelterIndex] = {
                                ...updatedSmelter,
                                running: false,
                                progressMs: 0,
                                fuelStarved: true,
                            };
                            return;
                        }

                        // Reduce cycles if not enough energy or input for all ready cycles
                        const maxByEnergy = Math.floor(availableEnergy / energyPerCycle);
                        const maxByInput = calculateMaxCyclesByInput(inputResource, inputQuantity, cyclesReady);
                        possibleCycles = Math.min(maxByEnergy, maxByInput);

                        // Check if we have no input resources - if so, mark as input starved and don't consume fuel
                        if (maxByInput <= 0) {
                            newSmelterState.find(s => s.resourceType === smelterState.resourceType)!.smelters[smelterIndex] = {
                                ...updatedSmelter,
                                inputStarved: true,
                            };
                            return;
                        }

                        // Consume fuel from buffer
                        const energyConsumed = possibleCycles * energyPerCycle;
                        const fuelConsumed = energyConsumed / FUEL_ENERGY_VALUES.coal;
                        updatedSmelter.fuelBuffer = (updatedSmelter.fuelBuffer ?? 0) - fuelConsumed;
                        pollutionDelta += fuelConsumed * POLLUTION_IMPACT.fuelUnit;
                    } else {
                        // Electric furnace or no fuel needed
                        possibleCycles = calculateMaxCyclesByInput(inputResource, inputQuantity, cyclesReady);
                        if (possibleCycles <= 0) {
                            newSmelterState.find(s => s.resourceType === smelterState.resourceType)!.smelters[smelterIndex] = {
                                ...updatedSmelter,
                                inputStarved: true,
                            };
                            return;
                        }
                    }

                    // Clear input starvation flag if we're processing successfully
                    updatedSmelter.inputStarved = false;

                    consumeInput(inputResource, inputQuantity, possibleCycles);
                    produceOutput(outputResource, recipe.outputQuantity, possibleCycles);
                    pollutionDelta += possibleCycles * POLLUTION_IMPACT.smelterCycle;

                    updatedSmelter.progressMs = calculateRemainingProgress(updatedSmelter.progressMs, recipe.cycleTimeMs);
                    newSmelterState.find(s => s.resourceType === smelterState.resourceType)!.smelters[smelterIndex] = updatedSmelter;
                });
            });

            // Process assembly setups
            const newAssemblySetups = normalized.assemblySetups.map(setup => {
                const assemblyDemand = setup.nodes.reduce((sum, node) => {
                    if (node.type !== "assembler" || node.running === false) return sum;
                    return sum + ASSEMBLER_POWER_USE * (node.overclocked ? OVERCLOCK_POWER : 1);
                }, 0);

                const baseDemand = Math.max(0, totalPowerDemand - externalDemand);
                const totalDemandWithAssembly = baseDemand + assemblyDemand;
                const setupPowerRatio = totalDemandWithAssembly === 0 ? 1 : Math.min(1, powerSupply / totalDemandWithAssembly);

                // Fill input buffers from source nodes each tick
                const nodesWithFilledBuffers = setup.nodes.map(node => {
                    if (node.type !== "assembler" || !node.recipeId) return node;

                    const recipe = ASSEMBLY_RECIPES[node.recipeId];
                    if (!recipe) return node;

                    // Initialize buffers if needed
                    const currentBuffers = node.inputBuffers ?? {};
                    const updatedBuffers = {...currentBuffers};

                    // Fill buffers from incoming source connections
                    recipe.ingredients.forEach(ingredient => {
                        const bufferCapacity = ingredient.quantity * 2; // 2x ingredient requirement
                        const currentAmount = updatedBuffers[ingredient.itemId] ?? 0;

                        if (currentAmount >= bufferCapacity) return; // Buffer full

                        const incomingConnections = setup.connections.filter(
                            conn => conn.toId === node.id && conn.itemId === ingredient.itemId
                        );

                        incomingConnections.forEach(conn => {
                            const sourceNode = setup.nodes.find(n => n.id === conn.fromId);
                            if (!sourceNode) return;

                            if (sourceNode.type === "source") {
                                // Source node - provides direct flow from smelters
                                const provision = calculateSourceProvision(
                                    sourceNode,
                                    normalized.assemblySetups,
                                    setup.id,
                                    newSmelterState
                                );

                                // Add to buffer (capped at capacity)
                                const spaceAvailable = bufferCapacity - currentAmount;
                                const amountToAdd = Math.min(provision, spaceAvailable);
                                updatedBuffers[ingredient.itemId] = currentAmount + amountToAdd;
                            }
                        });
                    });

                    return {...node, inputBuffers: updatedBuffers};
                });

                // Track output buffer consumption: Map<nodeId, Map<itemId, amount>>
                const outputBufferConsumption = new Map<string, Map<string, number>>();

                const updatedNodes = nodesWithFilledBuffers.map(node => {
                    if (node.type !== "assembler" || !node.recipeId || node.running === false) {
                        return node;
                    }
                    const recipe = ASSEMBLY_RECIPES[node.recipeId];
                    if (!recipe) return node;
                    if (!nodeHasIngredients(setup, node)) {
                        return {...node, progressMs: 0};
                    }
                    const speed = (node.overclocked ? OVERCLOCK_SPEED : 1) * setupPowerRatio;
                    const progressed = (node.progressMs ?? 0) + TIME_INCREMENT_MS * speed;
                    if (progressed < recipe.durationMs) {
                        return {...node, progressMs: progressed};
                    }
                    const dueCycles = Math.max(1, Math.floor(progressed / recipe.durationMs));

                    // Check output buffer space (capacity = 2x recipe output quantity)
                    const outputBufferCapacity = recipe.result.quantity * 2;
                    const currentOutputBuffer = node.outputBuffer?.[recipe.result.itemId] ?? 0;
                    const outputBufferSpace = outputBufferCapacity - currentOutputBuffer;
                    const maxCyclesFromOutputBuffer = Math.floor(outputBufferSpace / recipe.result.quantity);

                    // Calculate how many WHOLE cycles we can afford based on available materials AND output buffer space
                    // Check buffers for source connections, inventory for inventory/assembler connections
                    const affordableCycles = recipe.ingredients.reduce((min, ingredient) => {
                        const incomingConnections = setup.connections.filter(
                            conn => conn.toId === node.id && conn.itemId === ingredient.itemId
                        );

                        let availableForThisIngredient = 0;

                        incomingConnections.forEach(conn => {
                            const sourceNode = setup.nodes.find(n => n.id === conn.fromId);
                            if (!sourceNode) return;

                            if (sourceNode.type === "source") {
                                // Source node - check buffer (filled from smelter flow each tick)
                                const bufferAmount = node.inputBuffers?.[ingredient.itemId] ?? 0;
                                availableForThisIngredient += bufferAmount;
                            } else if (sourceNode.type === "inventory") {
                                // Inventory node - consumes from global inventory
                                const inventoryItem = findInventoryQuantity(newInventory, ingredient.itemId);
                                availableForThisIngredient += inventoryItem?.quantity ?? 0;
                            } else if (sourceNode.type === "assembler") {
                                // Assembler node - consumes from source assembler's output buffer
                                const bufferAmount = sourceNode.outputBuffer?.[ingredient.itemId] ?? 0;
                                availableForThisIngredient += bufferAmount;
                            }
                        });

                        const available = Math.floor(availableForThisIngredient / ingredient.quantity);
                        return Math.min(min, available);
                    }, Math.min(dueCycles, maxCyclesFromOutputBuffer));
                    if (affordableCycles <= 0) {
                        return {...node, progressMs: recipe.durationMs};
                    }
                    // Consume ingredients from buffers (for source connections), output buffers (for assembler connections), or inventory (for inventory connections)
                    const updatedInputBuffers = {...(node.inputBuffers ?? {})};
                    recipe.ingredients.forEach(ingredient => {
                        const amountToConsume = ingredient.quantity * affordableCycles;

                        const incomingConnections = setup.connections.filter(
                            conn => conn.toId === node.id && conn.itemId === ingredient.itemId
                        );

                        incomingConnections.forEach(conn => {
                            const sourceNode = setup.nodes.find(n => n.id === conn.fromId);
                            if (!sourceNode) return;

                            if (sourceNode.type === "source") {
                                // Source connection - consume from input buffer
                                const currentBuffer = updatedInputBuffers[ingredient.itemId] ?? 0;
                                const consumed = Math.min(currentBuffer, amountToConsume);
                                updatedInputBuffers[ingredient.itemId] = currentBuffer - consumed;
                            } else if (sourceNode.type === "inventory") {
                                // Inventory connection - consume from inventory
                                const inventoryItem = findInventoryQuantity(newInventory, ingredient.itemId);
                                if (inventoryItem && amountToConsume > 0) {
                                    const consumed = Math.min(inventoryItem.quantity, amountToConsume);
                                    inventoryItem.quantity -= consumed;
                                }
                            } else if (sourceNode.type === "assembler") {
                                // Assembler connection - consume from source assembler's output buffer
                                const sourceOutputBuffer = sourceNode.outputBuffer?.[ingredient.itemId] ?? 0;
                                const consumed = Math.min(sourceOutputBuffer, amountToConsume);

                                // Track consumption for later application
                                if (consumed > 0) {
                                    if (!outputBufferConsumption.has(sourceNode.id)) {
                                        outputBufferConsumption.set(sourceNode.id, new Map());
                                    }
                                    const nodeConsumption = outputBufferConsumption.get(sourceNode.id)!;
                                    const currentConsumption = nodeConsumption.get(ingredient.itemId) ?? 0;
                                    nodeConsumption.set(ingredient.itemId, currentConsumption + consumed);
                                }
                            }
                        });
                    });
                    const resultQty = recipe.result.quantity * affordableCycles;

                    // Add production to output buffer
                    const updatedOutputBuffer = {...(node.outputBuffer ?? {})};
                    const currentOutput = updatedOutputBuffer[recipe.result.itemId] ?? 0;
                    updatedOutputBuffer[recipe.result.itemId] = currentOutput + resultQty;

                    const leftover = progressed - affordableCycles * recipe.durationMs;
                    // Set production timestamp for pulse animation
                    const lastProductionTime = Date.now();
                    return {...node, progressMs: leftover, inputBuffers: updatedInputBuffers, outputBuffer: updatedOutputBuffer, lastProductionTime};
                });

                // Second pass: Apply output buffer consumption and transfer to inventory
                const finalNodes = updatedNodes.map(node => {
                    if (node.type !== "assembler") return node;

                    const updatedOutputBuffer = {...(node.outputBuffer ?? {})};
                    const updatedTransferTimes = {...(node.lastInventoryTransferTime ?? {})};

                    // Apply tracked consumption from downstream assemblers
                    if (outputBufferConsumption.has(node.id)) {
                        const consumption = outputBufferConsumption.get(node.id)!;
                        consumption.forEach((amount, itemId) => {
                            const currentBuffer = updatedOutputBuffer[itemId] ?? 0;
                            updatedOutputBuffer[itemId] = Math.max(0, currentBuffer - amount);
                        });
                    }

                    // Transfer output buffer to inventory when connected to inventory node
                    setup.connections.forEach(conn => {
                        if (conn.fromId !== node.id) return;

                        const targetNode = updatedNodes.find(n => n.id === conn.toId);
                        if (targetNode?.type === "inventory") {
                            const amount = updatedOutputBuffer[conn.itemId] ?? 0;
                            if (amount > 0) {
                                const inventoryItem = findInventoryQuantity(newInventory, conn.itemId);
                                if (inventoryItem) {
                                    inventoryItem.quantity += amount;
                                    updatedOutputBuffer[conn.itemId] = 0;
                                    // Track transfer time for animation (min 0.5s blink)
                                    updatedTransferTimes[conn.itemId] = Date.now();
                                }
                            }
                        }
                    });

                    return {...node, outputBuffer: updatedOutputBuffer, lastInventoryTransferTime: updatedTransferTimes};
                });

                return {...setup, nodes: finalNodes};
            });

            const fractionalFuelUsed = fuelUsed + newPowerMachines.waterBoilers.reduce((sum, boiler) => {
                return sum + (boiler.running ? boiler.progressMs : 0);
            }, 0);

            const powerStats: PowerSnapshot = {
                demand: totalPowerDemand,
                supply: powerSupply,
                ratio: powerRatio,
                externalDemand,
                steamDemand,
                steamUsed,
                steamProduced,
                steamCapacity,
                waterProduced,
                waterUsed,
                fuelUsedPerSecond: fractionalFuelUsed / secondsPerTick,
            };

            const pollution = accumulatePollution(normalized.pollution, pollutionDelta);

            // ========== CRAFTING QUEUE PROCESSING ==========
            const CRAFTING_POWER = 0.25; // 25% speed = 4x duration
            const newCraftingQueue = [...normalized.craftingQueue];

            if (newCraftingQueue.length > 0) {
                const topItem = newCraftingQueue[0];
                const recipe = CRAFTING_RECIPES.find(r => r.id === topItem.recipeId);

                if (!recipe) {
                    // Invalid recipe - remove from queue
                    newCraftingQueue.shift();
                } else {
                    // Check if we have all ingredients
                    const hasIngredients = recipe.ingredients.every(ing => {
                        const item = findInventoryQuantity(newInventory, ing.itemId);
                        return item && item.quantity >= ing.quantity;
                    });

                    if (!hasIngredients) {
                        // Mark as missing ingredients but don't progress
                        newCraftingQueue[0] = {...topItem, missingIngredients: true};
                    } else {
                        // Progress the craft
                        const effectiveDuration = recipe.durationMs / CRAFTING_POWER;
                        newCraftingQueue[0] = {
                            ...topItem,
                            progressMs: topItem.progressMs + TIME_INCREMENT_MS,
                            missingIngredients: false
                        };

                        // Check if craft is complete
                        if (newCraftingQueue[0].progressMs >= effectiveDuration) {
                            // Consume ingredients
                            recipe.ingredients.forEach(ing => {
                                const item = findInventoryQuantity(newInventory, ing.itemId);
                                if (item) item.quantity -= ing.quantity;
                            });

                            // Add result to inventory
                            const resultItem = findInventoryQuantity(newInventory, recipe.result.itemId);
                            if (resultItem) {
                                resultItem.quantity += recipe.result.quantity;
                            }

                            // Remove from queue
                            newCraftingQueue.shift();

                            // Add pollution
                            pollutionDelta += 0.5;

                            // Re-check the new top item's ingredients (if queue not empty)
                            if (newCraftingQueue.length > 0) {
                                const newTopItem = newCraftingQueue[0];
                                const newTopRecipe = CRAFTING_RECIPES.find(r => r.id === newTopItem.recipeId);
                                if (newTopRecipe) {
                                    const hasIngredientsNow = newTopRecipe.ingredients.every(ing => {
                                        const item = findInventoryQuantity(newInventory, ing.itemId);
                                        return item && item.quantity >= ing.quantity;
                                    });
                                    newCraftingQueue[0] = {...newTopItem, missingIngredients: !hasIngredientsNow};
                                }
                            }
                        }
                    }
                }
            }

            return {
                ...normalized,
                inventory: newInventory,
                minerState: newMinerState,
                smelterState: newSmelterState,
                powerMachines: newPowerMachines,
                powerStats,
                pollution,
                assemblySetups: newAssemblySetups,
                craftingQueue: newCraftingQueue,
            };
        });
    }, []);


    const tick = useCallback(() => {
        processTick();
    }, [processTick]);

    useEffect(() => {
        const interval = setInterval(tick, TIME_INCREMENT_MS);
        return () => clearInterval(interval);
    }, [tick]);

    const value: InventoryContextType = [inventory, setInventory];
    return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>;
}

export {InventoryProvider, useInventory}
